import { useEffect, useState } from 'react';
import { useCookieConsent } from '@/context/CookieConsentContext';

export function CookiePreferencesModal(): JSX.Element | null {
  const { isPreferencesOpen, setPreferencesOpen, preferences, updatePreferences } = useCookieConsent();
  const [analytics, setAnalytics] = useState(preferences.analytics);

  useEffect(() => {
    setAnalytics(preferences.analytics);
  }, [preferences.analytics, isPreferencesOpen]);

  useEffect(() => {
    if (!isPreferencesOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setPreferencesOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isPreferencesOpen, setPreferencesOpen]);

  if (!isPreferencesOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Cookie preferences"
        className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl"
      >
        <h2 className="text-lg font-bold text-gray-900">Cookie Preferences</h2>
        <p className="mt-2 text-sm text-gray-600">
          Under GDPR and the Philippine Data Privacy Act (RA 10173), you can control optional
          cookies used for analytics.
        </p>

        <div className="mt-5 space-y-4">
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">Necessary cookies</p>
                <p className="mt-1 text-sm text-gray-600">Required for security and core site behavior.</p>
              </div>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">Always active</span>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-900">Analytics cookies</p>
                <p className="mt-1 text-sm text-gray-600">Google Analytics, Google Tag Manager, and Microsoft Clarity.</p>
              </div>
              <button
                type="button"
                onClick={() => setAnalytics((prev) => !prev)}
                aria-pressed={analytics}
                className={`min-w-[70px] rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  analytics ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {analytics ? 'Enabled' : 'Disabled'}
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4 opacity-80">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-900">Marketing cookies</p>
                <p className="mt-1 text-sm text-gray-600">Not used at launch.</p>
              </div>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">Disabled</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setPreferencesOpen(false)}
            className="rounded-lg px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => updatePreferences({ analytics, marketing: false })}
            className="rounded-lg bg-[#1e3a5f] px-4 py-2 font-semibold text-white transition-colors hover:bg-[#152a45]"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}
